import { DEFAULT_ACTION_ITEM_CATEGORY_CATALOG } from "./actionItemCategories";
import { summarizeMeetingPreferences } from "./shared/meetingPreferences";
import { RECIPIENT_PROFILE_STYLE_OPTIONS } from "./shared/performerRecipientProfile";

const countDriveUrls = (driveInput) =>
  String(driveInput || "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("http")).length;

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB = "hub";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_ABOUT_YOU = "about-you";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_TASK_LABELS = "task-labels";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_DRAFT_STYLE = "draft-style";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_MEETING_PREFS = "meeting-prefs";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_GMAIL_DRAFTS = "gmail-drafts";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_CALENDAR = "calendar";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_SLACK_SEND = "slack-send";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_REFERENCE_DOCS = "reference-docs";

export const ACTION_ITEMS_SETTINGS_GENERAL_SCREENS = [
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_ABOUT_YOU,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_TASK_LABELS,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_DRAFT_STYLE,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_MEETING_PREFS,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_GMAIL_DRAFTS,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_CALENDAR,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_SLACK_SEND,
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_REFERENCE_DOCS,
];

const HUB_SCREEN_TITLES = {
  [ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_ABOUT_YOU]: "About you",
  [ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_TASK_LABELS]: "Task labels",
  [ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_DRAFT_STYLE]: "Draft style",
  [ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_MEETING_PREFS]: "Meeting preferences",
  [ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_GMAIL_DRAFTS]: "Gmail drafts",
  [ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_CALENDAR]: "Google Calendar",
  [ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_SLACK_SEND]: "Slack",
  [ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_REFERENCE_DOCS]: "Reference documents",
};

/**
 * Resolve General settings drill-down title for extension navigation.
 *
 * @example
 * resolveGeneralSettingsScreenTitle("gmail-drafts");
 * // "Gmail drafts"
 */
export const resolveGeneralSettingsScreenTitle = (screen) =>
  HUB_SCREEN_TITLES[screen] || "General";

const truncateSummary = (text, max = 52) => {
  const normalized = String(text || "").trim().replace(/\s+/g, " ");
  if (!normalized) return "";
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}…`;
};

/**
 * Build hub row summaries for extension General settings.
 *
 * @example
 * buildGeneralSettingsHubSummaries({
 *   profileDescription: "IC engineer at Task Mantis",
 *   categoryCatalog: ["Work", "Personal"],
 *   recipientStyle: "concise",
 *   gmailReady: false,
 *   driveInput: "https://docs.google.com/document/d/abc",
 *   contextFiles: [{ filename: "deck.pdf" }],
 * });
 * // { aboutYou: "IC engineer at Task Mantis", taskLabels: "Work, Personal", ... }
 */
export const buildGeneralSettingsHubSummaries = ({
  profileDescription = "",
  categoryCatalog = null,
  recipientStyle = "concise",
  workingHoursStart = null,
  workingHoursEnd = null,
  gmailReady = false,
  calendarReady = false,
  slackReady = false,
  driveInput = "",
  contextFiles = [],
}) => {
  const labels = Array.isArray(categoryCatalog) ? categoryCatalog : DEFAULT_ACTION_ITEM_CATEGORY_CATALOG;
  const driveUrlCount = countDriveUrls(driveInput);
  const fileCount = Array.isArray(contextFiles) ? contextFiles.length : 0;
  const draftLabel =
    RECIPIENT_PROFILE_STYLE_OPTIONS.find((option) => option.id === recipientStyle)?.label || "Concise";

  const referenceParts = [];
  if (driveUrlCount > 0) {
    referenceParts.push(`${driveUrlCount} Drive link${driveUrlCount === 1 ? "" : "s"}`);
  }
  if (fileCount > 0) {
    const firstFile = contextFiles[0]?.filename;
    referenceParts.push(
      fileCount === 1 && firstFile ? firstFile : `${fileCount} uploaded file${fileCount === 1 ? "" : "s"}`
    );
  }

  return {
    aboutYou: truncateSummary(profileDescription) || "Add your role and priorities",
    taskLabels: labels.join(", "),
    draftStyle: draftLabel,
    meetingPrefs: summarizeMeetingPreferences(workingHoursStart, workingHoursEnd),
    gmailDrafts: gmailReady ? "Connected" : "Not connected",
    calendar: calendarReady ? "Connected" : "Not connected",
    slackSend: slackReady ? "Connected" : "Not connected",
    referenceDocs: referenceParts.length ? referenceParts.join(" · ") : "Optional Drive links or files",
  };
};
